import { api, type ApiResponse } from '../client';

export interface User {
  uuid: string;
  full_name: string;
  email: string;
  phone?: string;
  user_status?: {
    uuid: string;
    name: string;
  };
  user_role?: {
    uuid: string;
    name: string;
  };
  email_verified_at?: string;
  created_at?: string;
  job_title?: string;
  location?: string;
  department?: string;
  last_donation_request_date?: string;
  organization?: {
    type: 'employer' | 'charity';
    data: {
      uuid: string;
      name: string;
      [key: string]: any;
    };
  } | null;
}

export interface UserFilters {
  page?: number;
  per_page?: number;
  phrase?: string;
  status?: string;
  role?: string;
  employer?: string;
}

export interface UsersResponse {
  success: boolean;
  message?: string;
  data: User[];
  paging: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    from: number;
    to: number;
  };
  errors?: string[];
}

export interface DonationRequest {
  uuid: string;
  leave_hours: number;
  leave_value: number;
  employer_top_up?: number;
  created_at: string;
  updated_at: string;
  charity?: {
    uuid: string;
    name: string;
  };
  employer?: {
    uuid: string;
    name: string;
  };
  donation_request_status?: {
    uuid: string;
    name: string;
  };
}

export interface UserProfile extends User {
  total_donations?: number;
  total_donated_hours?: number;
  total_donation_amount?: number;
  last_donation_date?: string;
  last_donation_request_date?: string;
  employers?: Array<{
    uuid: string;
    name: string;
  }>;
  donation_requests?: DonationRequest[];
}

const UsersService = {
  async getUsers(filters: UserFilters = {}): Promise<UsersResponse> {
    try {
      const params = new URLSearchParams();
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.per_page) params.append('per_page', filters.per_page.toString());
      if (filters.phrase) params.append('phrase', filters.phrase);
      if (filters.status) params.append('status', filters.status);
      if (filters.role) params.append('role', filters.role);
      if (filters.employer) params.append('employer', filters.employer);

      const response = await api.get<UsersResponse>(`/user?${params.toString()}`);
      const data = response.data.data;
      const items = (data && typeof data === 'object' && 'items' in data ? (data as any).items : Array.isArray(data) ? data : []) ?? [];
      const paging = (data && typeof data === 'object' && 'paging' in data ? (data as any).paging : (response.data as any).paging) ?? undefined;
      return {
        success: true,
        data: items,
        paging,
        message: 'Users retrieved successfully'
      };
    } catch (error: any) {
      return {
        success: false,
        data: [],
        paging: { current_page: 0, last_page: 0, per_page: 15, total: 0, from: 0, to: 0 },
        message: error?.response?.data?.message || error.message || 'Failed to load users'
      };
    }
  },

  async getUser(uuid: string): Promise<ApiResponse<UserProfile>> {
    const response = await api.get<ApiResponse<UserProfile>>(`/user/${uuid}`);
    return response.data;
  },

  async updateUser(uuid: string, data: Partial<User>): Promise<ApiResponse<User>> {
    const response = await api.put<ApiResponse<User>>(`/user/${uuid}`, data);
    return response.data;
  },

  async updateUserStatus(uuid: string, statusUuid: string): Promise<ApiResponse<UserProfile>> {
    const response = await api.put<ApiResponse<UserProfile>>(`/user/${uuid}`, { user_status_uuid: statusUuid });
    return response.data;
  },

  async deleteUser(uuid: string): Promise<ApiResponse<null>> {
    const response = await api.delete<ApiResponse<null>>(`/user/${uuid}`);
    return response.data;
  },
};

export default UsersService;

